import * as React from 'react';

import { AppRoutes } from 'constants/routes';

import { getAssetString, getEnvClassicAssetData } from 'helpers/assets';
import { createLumen } from 'helpers/token';

import useAuthStore from 'store/authStore/useAuthStore';

import { ModalService } from 'services/globalServices';

import ChooseLoginMethodModal from 'web/modals/auth/ChooseLoginMethodModal';
import { prefetchRoute } from 'web/routing/prefetchRoute';

import IconProfile from 'assets/icons/nav/icon-profile.svg';

import ActiveAssetProposal from 'components/Header/ActiveAssetProposal/ActiveAssetProposal';
import { ActiveProposals } from 'components/Header/ActiveProposals/ActiveProposals';
import ExpandedMenu from 'components/Header/ExpandedMenu/ExpandedMenu';

import AccountBlock from './AccountBlock';
import {
    Container,
    Aqua,
    NavLinkStyled,
    NavLinkWithCount,
    Divider,
    MainLink,
    NavLinks,
    RightBlock,
    MyAquarius,
    NotificationsSlot,
} from './Header.styled';
import { HeaderIndicators } from './Header.types';
import NotificationsBlock from './NotificationsBlock';
import useHeaderIndicators from './useHeaderIndicators';

const Links = ({ counts: proposalsCounts, proposal: activeAssetProposal }: HeaderIndicators) => {
    const activeAssetIndicator = activeAssetProposal ? (
        <ActiveAssetProposal proposal={activeAssetProposal} />
    ) : null;

    return (
        <>
            <NavLinkStyled
                to={AppRoutes.section.amm.link.index}
                style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                title="Pools"
            >
                Pools
            </NavLinkStyled>
            <NavLinkStyled
                to={AppRoutes.section.swap.to.index({
                    source: getAssetString(createLumen()),
                    destination: getEnvClassicAssetData('aqua').assetString,
                })}
                style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                title="Swap"
            >
                Swap
            </NavLinkStyled>

            <Divider />

            <ExpandedMenu
                title="Incentives"
                links={
                    <>
                        <NavLinkStyled
                            to={AppRoutes.page.rewards}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="AQUA Rewards"
                        >
                            AQUA Rewards
                        </NavLinkStyled>
                        <NavLinkStyled
                            to={AppRoutes.section.incentive.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Pool Incentives"
                        >
                            Pool Incentives
                        </NavLinkStyled>
                        <NavLinkStyled
                            to={AppRoutes.section.bribes.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Voting Incentives"
                        >
                            Voting Incentives
                        </NavLinkStyled>
                    </>
                }
            />

            <ExpandedMenu
                title="DAO"
                counts={proposalsCounts}
                indicator={activeAssetIndicator}
                links={
                    <>
                        <NavLinkStyled
                            to={AppRoutes.page.vote}
                            exact="true"
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Liquidity Voting"
                        >
                            Liquidity Voting
                        </NavLinkStyled>

                        <NavLinkWithCount>
                            <NavLinkStyled
                                to={AppRoutes.section.assetRegistry.link.index}
                                style={({ isActive }) =>
                                    isActive ? { fontWeight: 700 } : undefined
                                }
                                title="Asset Registry"
                            >
                                Asset Registry
                            </NavLinkStyled>
                            {activeAssetIndicator}
                        </NavLinkWithCount>

                        <NavLinkWithCount>
                            <NavLinkStyled
                                to={AppRoutes.section.governance.link.index}
                                style={({ isActive }) =>
                                    isActive ? { fontWeight: 700 } : undefined
                                }
                                title="DAO Proposals"
                            >
                                DAO Proposals
                            </NavLinkStyled>
                            <ActiveProposals
                                discussionCount={proposalsCounts.discussion}
                                activeCount={proposalsCounts.active}
                            />
                        </NavLinkWithCount>

                        <NavLinkStyled
                            to={AppRoutes.section.delegate.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="ICE delegation"
                        >
                            ICE Delegation
                        </NavLinkStyled>
                    </>
                }
            />

            <ExpandedMenu
                title="AQUA & ICE"
                links={
                    <>
                        <NavLinkStyled
                            to={AppRoutes.section.locker.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Lock AQUA"
                        >
                            Lock AQUA
                        </NavLinkStyled>
                        <NavLinkStyled
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="About"
                            to={AppRoutes.page.token}
                        >
                            About
                        </NavLinkStyled>
                    </>
                }
            />
        </>
    );
};

const Header = (): React.ReactNode => {
    const { isLogged } = useAuthStore();
    const indicators = useHeaderIndicators();
    const preloadLink = (event: React.SyntheticEvent) => {
        const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
        if (link) prefetchRoute(link.href);
    };

    const onMyAquariusClick = (e: React.MouseEvent) => {
        if (!isLogged) {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();

            return ModalService.openModal(ChooseLoginMethodModal, {
                redirectURL: AppRoutes.page.account,
            });
        }
    };
    return (
        <Container
            onFocusCapture={preloadLink}
            onPointerOver={event => {
                if (event.pointerType === 'mouse') preloadLink(event);
            }}
        >
            <MainLink to={AppRoutes.page.main}>
                <Aqua />
            </MainLink>

            <NavLinks>
                <Links {...indicators} />
            </NavLinks>

            <RightBlock>
                <MyAquarius onClick={onMyAquariusClick} to={AppRoutes.page.account}>
                    <IconProfile />
                    <div>Dashboard</div>
                </MyAquarius>
                <NotificationsSlot>
                    <NotificationsBlock />
                </NotificationsSlot>
                <AccountBlock navLinks={<Links {...indicators} />} />
            </RightBlock>
        </Container>
    );
};

export default Header;
